/**
 * Store Detail — Gallery Slider + Lightbox
 * Uses the theme's bundled Swiper (ES module)
 * Pattern matches swiper.js component initialization
 */
import Swiper from "swiper";
import {
  Navigation,
  Pagination,
  Thumbs,
  FreeMode,
} from "swiper/modules";

// ── Gallery Thumbs ──
const initGalleryThumbs = () => {
  const thumbsEl = document.querySelector(".sd__gallery-thumbs");
  if (!thumbsEl) return null;

  return new Swiper(thumbsEl, {
    modules: [FreeMode],
    spaceBetween: 8,
    slidesPerView: 5,
    freeMode: true,
    watchSlidesProgress: true,
    observer: true,
    observeParents: true,
    breakpoints: {
      0: { slidesPerView: 4, spaceBetween: 6 },
      640: { slidesPerView: 5, spaceBetween: 8 },
      1024: { slidesPerView: 5, spaceBetween: 8 },
    },
  });
};

// ── Gallery Main ──
const initGalleryMain = (thumbsSwiper) => {
  const mainEl = document.querySelector(".sd__gallery-main");
  if (!mainEl) return null;

  const opts = {
    modules: [Navigation, Thumbs],
    slidesPerView: 1,
    spaceBetween: 0,
    loop: false,
    observer: true,
    observeParents: true,
    grabCursor: true,
    navigation: {
      nextEl: mainEl.querySelector(".swiper-button-next"),
      prevEl: mainEl.querySelector(".swiper-button-prev"),
    },
  };

  if (thumbsSwiper) {
    opts.thumbs = { swiper: thumbsSwiper };
  }

  return new Swiper(mainEl, opts);
};

// ── Lightbox ──
const initLightbox = () => {
  const lightbox = document.getElementById("sd-lightbox");
  const lbSwiperEl = document.getElementById("sd-lightbox-swiper");
  const lbCounter = document.getElementById("sd-lb-current");

  if (!lightbox || !lbSwiperEl) return;

  let lbSwiper = null;

  const openLightbox = (index) => {
    lightbox.hidden = false;
    document.body.style.overflow = "hidden";

    if (!lbSwiper) {
      lbSwiper = new Swiper(lbSwiperEl, {
        modules: [Navigation, Pagination],
        slidesPerView: 1,
        spaceBetween: 0,
        initialSlide: index || 0,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: lbSwiperEl.querySelector(".swiper-button-next"),
          prevEl: lbSwiperEl.querySelector(".swiper-button-prev"),
        },
        pagination: {
          el: lbSwiperEl.querySelector(".swiper-pagination"),
          clickable: true,
        },
        on: {
          slideChange: function () {
            if (lbCounter) lbCounter.textContent = this.activeIndex + 1;
          },
        },
      });
    } else {
      lbSwiper.slideTo(index || 0, 0);
    }

    if (lbCounter) lbCounter.textContent = (index || 0) + 1;

    // Focus trap
    setTimeout(() => {
      lightbox.querySelector(".sd__lightbox-close")?.focus();
    }, 100);
  };

  const closeLightbox = () => {
    lightbox.hidden = true;
    document.body.style.overflow = "";
  };

  // Click zoom buttons → open lightbox
  document.querySelectorAll(".sd__gallery-zoom").forEach((btn) => {
    btn.addEventListener("click", () => {
      openLightbox(parseInt(btn.dataset.index) || 0);
    });
  });

  // Zoom toggle
  const zoomBtn = lightbox.querySelector(".sd__lightbox-zoom");
  if (zoomBtn) {
    zoomBtn.addEventListener("click", () => {
      lbSwiperEl.classList.toggle("is-zoomed");
    });
  }

  // Close button
  lightbox
    .querySelector(".sd__lightbox-close")
    ?.addEventListener("click", () => {
      lbSwiperEl.classList.remove("is-zoomed");
      closeLightbox();
    });

  // Click backdrop
  lightbox.addEventListener("click", (e) => {
    if (e.target === lightbox) {
      lbSwiperEl.classList.remove("is-zoomed");
      closeLightbox();
    }
  });

  // Escape key
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !lightbox.hidden) {
      lbSwiperEl.classList.remove("is-zoomed");
      closeLightbox();
    }
  });
};

// ── Init ──
document.addEventListener("DOMContentLoaded", () => {
  const gallery = document.getElementById("sd-gallery");
  if (!gallery) return;

  const thumbsSwiper = initGalleryThumbs();
  initGalleryMain(thumbsSwiper);
  initLightbox();
});
